import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Facebook, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FB_RESULT_KEY } from "@/pages/FbDone";
import api, { errMsg } from "@/lib/api";

const RESULTS = {
  ok: ["success", "Facebook Page berjaya disambung"],
  pilih: ["info", "Pilih Page yang anda mahu guna"],
  batal: ["error", "Sambungan Facebook dibatalkan"],
  tamat: ["error", "Sesi sambungan tamat. Cuba lagi."],
  gagal: ["error", "Facebook menolak sambungan. Semak tetapan App anda."],
  tiadapage: ["error", "Tiada Page dijumpai. Pastikan anda admin Page dan beri kebenaran semasa log masuk."],
};

function openPopup() {
  const w = 600, h = 720;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  return window.open("", "fb-connect", `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}

export default function FacebookConnect({ onConnected }) {
  const [params, setParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState("");
  const handled = useRef(0);

  const loadPages = () => api.get("/facebook/pages").then((r) => setPages(r.data)).catch(() => {});

  const handle = (status) => {
    if (!RESULTS[status]) return;
    const [type, msg] = RESULTS[status];
    toast[type](msg);
    setBusy("");
    loadPages();
    if (status === "ok" || status === "pilih") onConnected();
  };

  useEffect(() => {
    const fb = params.get("fb");
    if (fb) { handle(fb); params.delete("fb"); setParams(params, { replace: true }); } else loadPages();

    const fromStorage = (raw) => {
      try {
        const { status, t } = JSON.parse(raw);
        if (t > handled.current) { handled.current = t; handle(status); }
      } catch { /* ignore */ }
    };
    const onStorage = (e) => e.key === FB_RESULT_KEY && e.newValue && fromStorage(e.newValue);
    const onMessage = (e) => {
      if (e.origin === window.location.origin && e.data?.type === "fb-connect") fromStorage(JSON.stringify({ status: e.data.status, t: Date.now() }));
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("message", onMessage);
    return () => { window.removeEventListener("storage", onStorage); window.removeEventListener("message", onMessage); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy("connect");
    handled.current = Date.now();
    const popup = openPopup();
    try {
      const { data } = await api.get("/facebook/start");
      if (popup) {
        popup.location.href = data.url;
        const timer = setInterval(() => { if (popup.closed) { clearInterval(timer); setTimeout(() => setBusy((b) => (b === "connect" ? "" : b)), 800); } }, 700);
      } else {
        window.location.assign(data.url);
      }
    } catch (e) { popup?.close(); toast.error(errMsg(e)); setBusy(""); }
  };

  const select = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/facebook/pages/select", { page_id: id });
      toast.success(`Page dipilih: ${data.page_name}`);
      await loadPages();
      onConnected();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-3">
      <Button onClick={connect} disabled={!!busy} className="h-11 w-full bg-[#1877F2] font-semibold hover:bg-[#166FE5] sm:w-auto" data-testid="settings-fb-connect-btn">
        {busy === "connect" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Facebook className="mr-2 h-4 w-4" />}
        {busy === "connect" ? "Menunggu log masuk Facebook..." : "Sambung dengan Facebook"}
      </Button>
      {pages.length > 1 && (
        <div className="space-y-2 rounded-lg border border-border p-3" data-testid="settings-fb-page-picker">
          <p className="text-xs text-muted-foreground">Page yang anda urus:</p>
          {pages.map((p) => (
            <button key={p.id} onClick={() => select(p.id)} disabled={!!busy} className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${p.selected ? "border-emerald-500/50 bg-emerald-500/10" : "border-border hover:bg-accent"}`} data-testid={`settings-fb-page-option-${p.id}`}>
              <span>{p.name}</span>
              {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : p.selected && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
